/**
 * Month periods for finance filters (`from`/`to` as "YYYY-MM"). Only UI defaults and
 * validation – the API computes all figures.
 */

const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** @param {unknown} value */
export function isYearMonth(value) {
  return typeof value === 'string' && YEAR_MONTH.test(value);
}

/** Date → "YYYY-MM" (local time). @param {Date} date */
export function toYearMonth(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The last `months` months including the current one, e.g. 12 → { from: '2025-11', to: '2026-10' }.
 * Mirrors the API default of PeriodRequest.
 * @param {number} [months=12] @param {Date} [today]
 */
export function lastMonths(months = 12, today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
  return { from: toYearMonth(from), to: toYearMonth(today) };
}

export const OP_TYPES = ['MIETE', 'BK', 'NEBENKOSTEN'];

/**
 * G-20/G-21: Betragsbasis. `gross` = Beträge wie gebucht (Standard der Kostenansicht), `net` = MwSt herausgerechnet
 * (Berechnung ausschließlich in der API). Kostenansicht: Standard gross; Vermietungsübersicht: Standard net.
 */
export const VAT_BASES = ['gross', 'net'];
export const VAT_BASIS_OPTIONS = [
  { value: 'gross', label: 'Brutto' },
  { value: 'net', label: 'Netto' },
];

/**
 * `validateSearch` for finance pages: `from`/`to` ("YYYY-MM"), `property` (numeric id), `op_type`, `vat` (gross|net).
 * Invalid values are dropped (the page then falls back to its defaults).
 * @param {Record<string, unknown>} search
 * @returns {{ from?: string, to?: string, property?: string, op_type?: string, vat?: string }}
 */
export function financeSearch(search) {
  /** @type {Record<string, string>} */
  const out = {};
  if (isYearMonth(search.from)) out.from = /** @type {string} */ (search.from);
  if (isYearMonth(search.to)) out.to = /** @type {string} */ (search.to);
  if (out.from && out.to && out.from > out.to) delete out.from;
  const property = String(search.property ?? '');
  if (/^\d+$/.test(property)) out.property = property;
  if (typeof search.op_type === 'string' && OP_TYPES.includes(search.op_type)) out.op_type = search.op_type;
  if (typeof search.vat === 'string' && VAT_BASES.includes(search.vat)) out.vat = search.vat;
  return out;
}
